import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert, Form, Input, Modal } from 'antd'

import {
  type CatalogImage,
  createImage,
  type ImageLabels,
  IMAGES_KEY,
  type NewImage,
  updateLabels,
} from '../api'
import { SourceFields } from './SourceFields'

export function ImageForm({
  image,
  onClose,
}: {
  image?: CatalogImage
  onClose: () => void
}) {
  const [form] = Form.useForm<NewImage>()
  const kind = Form.useWatch(['source', 'kind'], form)
  const createLabel =
    kind === 'HTTP' ? 'Create published HTTP image' : 'Create hidden image'
  const cache = useQueryClient()
  const mutation = useMutation({
    mutationFn: (values: NewImage) => {
      const labels: ImageLabels = {
        name: values.name,
        slug: values.slug,
        distribution: values.distribution,
        iconUrl: values.iconUrl,
      }
      if (image) return updateLabels(image.id, labels)
      const source =
        values.source.kind === 'HTTP'
          ? {
              kind: 'HTTP' as const,
              downloadUrl: values.source.downloadUrl,
              username: values.source.username,
              bootMode: values.source.bootMode,
            }
          : { ...values.source, sha256: values.source.sha256 || undefined }
      return createImage({ ...values, source })
    },
    retry: false,
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: IMAGES_KEY })
      onClose()
    },
  })
  return (
    <Modal
      open
      title={image ? `Edit labels: ${image.name}` : 'Create image'}
      width={620}
      okText={image ? 'Save labels' : createLabel}
      confirmLoading={mutation.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      maskClosable={!mutation.isPending}
      closable={!mutation.isPending}
      cancelButtonProps={{ disabled: mutation.isPending }}
    >
      {mutation.error && (
        <Alert
          type="error"
          role="alert"
          showIcon
          message={mutation.error.message}
          description="Inputs are preserved. If the connection was interrupted, refresh the catalog before retrying."
          style={{ marginBottom: 16 }}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        onValuesChange={(changed: { source?: { downloadUrl?: string } }) => {
          if (changed.source && 'downloadUrl' in changed.source) {
            form.setFieldValue(['source', 'virtualSize'], undefined)
            form.setFieldValue(['source', 'sha256'], undefined)
          }
        }}
        disabled={mutation.isPending}
        initialValues={image ?? { source: { kind: 'IMAGE', bootMode: 'EFI' } }}
        onFinish={(values) => mutation.mutate(values)}
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, whitespace: true }, { max: 64 }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="distribution"
          label="Distribution"
          rules={[{ required: true }, { min: 3 }]}
        >
          <Input placeholder="Ubuntu" />
        </Form.Item>
        <Form.Item
          name="slug"
          label="Slug"
          rules={[{ required: true }, { min: 5 }]}
        >
          <Input placeholder="ubuntu-24-04-x64" />
        </Form.Item>
        <Form.Item
          name="iconUrl"
          label="Icon URL"
          rules={[
            { required: true },
            { type: 'url' },
            { pattern: /^https?:\/\//, message: 'Use an HTTP or HTTPS URL' },
          ]}
        >
          <Input />
        </Form.Item>
        {!image && <SourceFields />}
      </Form>
    </Modal>
  )
}
